import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { Countries } from '@/utils/common';

import type { SavedAddressFormData } from './schema';
import type { AddressDetails } from '@/components/address-autocomplete';
import type { RootState } from '@/redux/store';
import type {
  CreateSavedAddressReq,
  SavedAddress,
} from '@/types/parcel/userData/saved-addresses.interface';

import { savedAddressFormSchema } from './schema';

interface UseSavedAddressFormModalProps {
  initialAddress?: SavedAddress | null;
  defaultType?: 'origin' | 'destination' | 'both';
  defaultCountry?: string;
  onSave: (data: CreateSavedAddressReq) => Promise<void>;
  onClose: () => void;
}

export const useSavedAddressFormModal = (props: UseSavedAddressFormModalProps) => {
  const { initialAddress, defaultType = 'both', defaultCountry, onSave, onClose } = props;
  const { profile } = useSelector((state: RootState) => state.parcel);
  const userCountry = (profile?.data?.country || defaultCountry || 'MEX') as Countries;

  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<SavedAddressFormData>({
    resolver: yupResolver(savedAddressFormSchema),
    mode: 'onChange',
    defaultValues: {
      alias: initialAddress?.alias || '',
      type: initialAddress?.type || defaultType,
      country: userCountry,
      address: initialAddress?.address || '',
      street_number: initialAddress?.street_number || '',
      interior_number: initialAddress?.interior_number || '',
      colony: initialAddress?.colony || '',
      zip_code: initialAddress?.zip_code || '',
      city: initialAddress?.city || '',
      state: initialAddress?.state || '',
      latitude: initialAddress?.latitude || 0,
      longitude: initialAddress?.longitude || 0,
      references: initialAddress?.references || '',
      contact: {
        first_name: initialAddress?.contact?.first_name || '',
        last_name: initialAddress?.contact?.last_name || '',
        phone: initialAddress?.contact?.phone || '+52',
        email: initialAddress?.contact?.email || '',
      },
    },
  });

  useEffect(() => {
    if (initialAddress) {
      reset({
        alias: initialAddress.alias || '',
        type: initialAddress.type || 'both',
        country: initialAddress.country || userCountry,
        address: initialAddress.address || '',
        street_number: initialAddress.street_number || '',
        interior_number: initialAddress.interior_number || '',
        colony: initialAddress.colony || '',
        zip_code: initialAddress.zip_code || '',
        city: initialAddress.city || '',
        state: initialAddress.state || '',
        latitude: initialAddress.latitude || 0,
        longitude: initialAddress.longitude || 0,
        references: initialAddress.references || '',
        contact: {
          first_name: initialAddress.contact?.first_name || '',
          last_name: initialAddress.contact?.last_name || '',
          phone: initialAddress.contact?.phone || '+52',
          email: initialAddress.contact?.email || '',
        },
      });
    } else {
      setValue('country', userCountry);
    }
  }, [initialAddress, userCountry, reset, setValue]);

  const onAddressSelect = (details: AddressDetails) => {
    setValue('address', details.address, { shouldValidate: true });
    setValue('zip_code', details.zipCode, { shouldValidate: true });
    setValue('city', details.city, { shouldValidate: true });
    setValue('state', details.state, { shouldValidate: true });
    setValue('colony', details.neighborhood, { shouldValidate: true });
    if (details.streetNumber) {
      setValue('street_number', details.streetNumber, { shouldValidate: true });
    }
    setValue('country', userCountry, { shouldValidate: true });
    setValue('latitude', details.latitude, { shouldValidate: true });
    setValue('longitude', details.longitude, { shouldValidate: true });
  };

  const onAddressChange = (addressText: string) => {
    setValue('address', addressText, { shouldValidate: true });
    if (!addressText || addressText.trim() === '') {
      setValue('street_number', '');
      setValue('colony', '');
      setValue('zip_code', '', { shouldValidate: true });
      setValue('city', '', { shouldValidate: true });
      setValue('state', '');
      setValue('latitude', 0, { shouldValidate: true });
      setValue('longitude', 0, { shouldValidate: true });
    }
  };

  const onSubmit = handleSubmit(async (formData: SavedAddressFormData) => {
    setSubmitting(true);
    try {
      const payload: CreateSavedAddressReq = {
        alias: formData.alias,
        type: formData.type,
        country: userCountry,
        address: formData.address,
        street_number: formData.street_number || undefined,
        interior_number: formData.interior_number || undefined,
        colony: formData.colony || undefined,
        zip_code: formData.zip_code,
        city: formData.city,
        state: formData.state || undefined,
        latitude: Number(formData.latitude),
        longitude: Number(formData.longitude),
        references: formData.references || undefined,
        contact: {
          first_name: formData.contact.first_name,
          last_name: formData.contact.last_name,
          phone: formData.contact.phone,
          email: formData.contact.email,
        },
      };

      await onSave(payload);
      onClose();
    } catch {
      // Error manejado en el hook padre
    } finally {
      setSubmitting(false);
    }
  });

  const handlePhoneChange = (newPhone: string) => {
    setValue('contact.phone', newPhone, { shouldValidate: true });
  };

  return {
    register,
    errors,
    watch,
    setValue,
    submitting,
    isValid,
    onSubmit,
    handlePhoneChange,
    isEditing: !!initialAddress,
    onAddressSelect,
    onAddressChange,
    userCountry,
  };
};

export default useSavedAddressFormModal;
